import { useMemo } from 'react';

import ProgressBar from '@ramonak/react-progress-bar';
import { useTranslation } from 'react-i18next';

import { OrganisationUsage } from '../../../api/types/backend-dto-types';
import TooltipIcon from '../../../components/atoms/TooltipIcon';
import { Chip } from '../../components/chip';
import { Skeleton } from '../../components/skeleton';
import { SubTitle } from '../../components/subtitle';
import { cn } from '../../lib/utils';

export function CreditUsage({ periodCreditUsage }: { periodCreditUsage: OrganisationUsage['periodCreditUsage'] }) {
  const { i18n } = useTranslation();

  const totalCreditsUsed = useMemo(() => {
    if (!periodCreditUsage) return 0;
    return periodCreditUsage.billable + periodCreditUsage.nonBillable;
  }, [periodCreditUsage]);

  const billableUsagePercentage = useMemo(() => {
    if (!periodCreditUsage) return 0;
    const total = periodCreditUsage.maxCredits ?? totalCreditsUsed;
    return (periodCreditUsage.billable / total) * 100;
  }, [periodCreditUsage]);

  const nonBillableUsagePercentage = useMemo(() => {
    if (!periodCreditUsage) return 0;
    const total = periodCreditUsage.maxCredits ?? totalCreditsUsed;
    return (periodCreditUsage.nonBillable / total) * 100;
  }, [periodCreditUsage]);

  // if (periodCreditUsage && billableUsagePercentage !== null && nonBillableUsagePercentage !== null)
  return (
    <div className="w-full">
      <div className="mb-2 flex items-center gap-2">
        <SubTitle text="Total Credit Usage" />
        {periodCreditUsage?.isUsingTrialPackage && (
          <Chip className="bg-teal-100 text-teal-700">Onboarding Package</Chip>
        )}
      </div>

      <p className="mt-1 text-sm text-gray-600">
        Credit usage for the{' '}
        {periodCreditUsage?.isUsingTrialPackage
          ? 'onboarding period.'
          : 'current billing period across your entire organisation.'}
      </p>

      <div className="relative mt-6">
        <div className="mb-2 flex items-end justify-between">
          <p className="flex h-6 items-end text-xl font-semibold">
            {periodCreditUsage ? (
              <span
                className={cn(
                  !periodCreditUsage.isUsingTrialPackage &&
                    periodCreditUsage.maxCredits &&
                    totalCreditsUsed > periodCreditUsage.maxCredits &&
                    'text-red-600',
                  'animate-in fade-in',
                )}
              >
                {new Intl.NumberFormat(i18n.language).format(totalCreditsUsed)}
              </span>
            ) : (
              <Skeleton className="mr-1 block h-full w-16 bg-gray-200" />
            )}

            {periodCreditUsage?.isUsingTrialPackage && periodCreditUsage.maxCredits && (
              <TooltipIcon
                side="right"
                triggerClassName="text-teal-500"
                tooltipContent={`The onboarding package has generous limits on usage. Currently this is set to ${new Intl.NumberFormat(
                  'en-US',
                  { notation: 'compact' },
                ).format(
                  periodCreditUsage.maxCredits,
                )} credits. If you feel this is not enough for your use case, reach out to support@wexler.ai.`}
              />
            )}

            {!periodCreditUsage?.isUsingTrialPackage && periodCreditUsage?.maxCredits && (
              <>
                <span className="inline-flex items-center gap-1.5 pb-0.25 pl-1 text-base text-gray-500">
                  {' '}
                  / {new Intl.NumberFormat('en-US', { notation: 'compact' }).format(periodCreditUsage.maxCredits)} limit
                  <TooltipIcon
                    tooltipContent={`${((totalCreditsUsed / periodCreditUsage.maxCredits) * 100).toFixed(1)}%`}
                  />
                </span>
              </>
            )}
          </p>

          {periodCreditUsage?.endDate && (
            <p className="animate-in fade-in mb-1 text-xs text-gray-500">
              usage resets on{' '}
              <span className="font-semibold">
                {new Intl.DateTimeFormat(i18n.language).format(new Date(periodCreditUsage.endDate))}
              </span>
            </p>
          )}
        </div>

        {/* Absolutely positioned on top of the below bar to show two both values (if we decide to do that) */}
        {/* Adding both billable and non-billable */}
        <ProgressBar
          completed={billableUsagePercentage + nonBillableUsagePercentage}
          height={'10px'}
          bgColor="var(--color-teal-400)"
          baseBgColor={periodCreditUsage?.isUsingTrialPackage ? 'var(--color-teal-100)' : 'var(--color-gray-200)'}
          className="w-full"
          isLabelVisible={false}
          animateOnRender={true}
        />

        {/* Non-billable sections and legend removed while non-billable not in use */}
        {/* <ProgressBar
            completed={billableUsagePercentage + nonBillableUsagePercentage}
            height={'10px'}
            bgColor="#349989"
            isLabelVisible={false}
            animateOnRender={true}
          /> */}

        <div className="mt-3 flex items-center gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-sm bg-teal-400"></div>
            <span className="text-gray-500">Credits used</span>
          </div>
          {/* <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-sm" style={{ backgroundColor: '#349989' }}></div>
              <span className="text-gray-500">Non-billable</span>
            </div> */}
        </div>
      </div>
    </div>
  );
}
